import React, { useState, useEffect } from "react";
import { empresaService } from "../services/empresaService";
import { Loading } from "./common/Loading";
import "./AdministrarEmpresas.css";

const emptyForm = { nombre: "", logo_url: "" };

function AdministrarEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmarEliminar, setConfirmarEliminar] = useState(null);

  useEffect(() => {
    cargarEmpresas();
  }, []);

  const cargarEmpresas = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await empresaService.obtenerTodas();
      setEmpresas(data);
    } catch (err) {
      console.error("Error al cargar empresas:", err);
      setError("Error loading companies. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (empresa) => {
    setEditing(empresa);
    setForm({
      nombre: empresa.nombre || "",
      logo_url: empresa.logo_url || "",
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      alert("Company name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        logo_url: form.logo_url.trim() || null,
      };

      if (editing) {
        await empresaService.actualizar(editing.id, payload);
      } else {
        await empresaService.crear(payload);
      }

      closeForm();
      await cargarEmpresas();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.detail || err?.message || "Error saving company";
      alert(typeof detail === "string" ? detail : "Error saving company");
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (empresaId) => {
    try {
      await empresaService.eliminar(empresaId);
      setConfirmarEliminar(null);
      await cargarEmpresas();
    } catch (err) {
      console.error("Error al eliminar empresa:", err);
      alert("Error deleting company. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="empresas-wrapper">
        <Loading fullScreen={true} />
      </div>
    );
  }

  return (
    <div className="empresas-wrapper">
      <main className="empresas-main">
        <div className="empresas-container">
          <div className="empresas-header-section">
            <h1 className="empresas-title">Companies</h1>
            <button className="btn-crear-empresa" onClick={openCreate}>
              + Create Company
            </button>
          </div>

          {error && <div className="empresas-error">{error}</div>}

          {empresas.length === 0 ? (
            <div className="empresas-empty">
              <p>No companies yet.</p>
              <button className="btn-crear-empresa" onClick={openCreate}>
                Create First Company
              </button>
            </div>
          ) : (
            <div className="empresas-grid">
              {empresas.map((empresa) => (
                <div key={empresa.id} className="empresa-card">
                  <div className="empresa-card-header">
                    <div className="empresa-card-identity">
                      {empresa.logo_url ? (
                        <img
                          src={empresa.logo_url}
                          alt={empresa.nombre}
                          className="empresa-logo"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="empresa-logo-placeholder">
                          {empresa.nombre.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="empresa-card-title">{empresa.nombre}</h3>
                        <span className="empresa-id">ID: {empresa.id}</span>
                      </div>
                    </div>
                    <div className="empresa-card-actions">
                      <button
                        className="btn-editar"
                        onClick={() => openEdit(empresa)}
                        title="Edit company"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-eliminar"
                        onClick={() => setConfirmarEliminar(empresa)}
                        title="Delete company"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="empresa-card-body">
                    <span className="stat-item">
                      📝 {empresa.examenes_count ?? 0} exams
                    </span>
                    {empresa.logo_url && (
                      <span className="empresa-logo-url" title={empresa.logo_url}>
                        🔗 Logo URL set
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {formOpen && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? "Edit Company" : "Create Company"}</h2>
              <button type="button" className="btn-cerrar-modal" onClick={closeForm}>
                ×
              </button>
            </div>
            <form className="empresa-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="empresa-nombre">Name *</label>
                <input
                  id="empresa-nombre"
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="e.g. Microsoft Azure"
                  required
                  maxLength={100}
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="empresa-logo">Logo URL</label>
                <input
                  id="empresa-logo"
                  type="url"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="https://..."
                  maxLength={512}
                  disabled={saving}
                />
              </div>
              {form.logo_url && (
                <div className="empresa-logo-preview">
                  <img src={form.logo_url} alt="Preview" />
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="btn-cancelar" onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-guardar" disabled={saving}>
                  {saving ? "Saving..." : editing ? "Save changes" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmarEliminar && (
        <div className="modal-overlay" onClick={() => setConfirmarEliminar(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete "{confirmarEliminar.nombre}"?
              Linked exams will keep working but without a company assigned.
            </p>
            <div className="modal-actions">
              <button className="btn-cancelar" onClick={() => setConfirmarEliminar(null)}>
                Cancel
              </button>
              <button
                className="btn-confirmar-eliminar"
                onClick={() => handleEliminar(confirmarEliminar.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdministrarEmpresas;
